import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { type QueryRef, useApolloClient, useMutation, useReadQuery } from '@apollo/client/react'
import { ConfirmDialog, ErrorAlert } from '@repo/shared-client/components/blocks'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Progress,
  Separator,
  Spinner,
} from '@repo/shared-client/components/ui'
import { createFileRoute, Link } from '@tanstack/react-router'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import dayjs from 'dayjs'
import { useState } from 'react'
import toast from 'react-hot-toast'
import type {
  PointsPageQuery,
  PointsPageQueryVariables,
  RedeemPointsForSupporterMonthMutation,
  RedeemPointsForSupporterMonthMutationVariables,
} from 'types/graphql'

const POINTS_PAGE_QUERY: TypedDocumentNode<PointsPageQuery, PointsPageQueryVariables> = gql`
  query PointsPageQuery {
    currentProfilePointBalance {
      id
      availablePoints
      lifetimeEarnedPoints
      redeemedPoints
      reversedPoints
      supporterMonthThreshold
      supporterDaysGranted
      pointsUntilSupporterMonth
      progressPercent
      canRedeemSupporterMonth
    }
    currentProfilePointTransactions(limit: 10) {
      id
      createdAt
      points
      type
      reason
      status
    }
  }
`

const REDEEM_POINTS_FOR_SUPPORTER_MONTH: TypedDocumentNode<
  RedeemPointsForSupporterMonthMutation,
  RedeemPointsForSupporterMonthMutationVariables
> = gql`
  mutation RedeemPointsForSupporterMonth($idempotencyKey: String!) {
    redeemPointsForSupporterMonth(idempotencyKey: $idempotencyKey) {
      id
      pointsSpent
      supporterDaysGranted
      grantedAt
    }
  }
`

export const Route = createFileRoute('/(auth)/(account-layout)/account/$profileId/points')({
  component: PointsPage,
  head: () =>
    seo({
      title: `Points | ${env.VITE_APP_NAME}`,
      description: 'Redeem contribution points for supporter status',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    }),
  loader: async ({ context }) => {
    const { queryRef } = await queryAndPreload(context, POINTS_PAGE_QUERY)
    return { queryRef }
  },
})

function PointsPage() {
  const { t } = useTranslation()
  const client = useApolloClient()
  const { queryRef } = Route.useLoaderData()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<PointsPageQuery>)
  const [redeemDialogOpen, setRedeemDialogOpen] = useState(false)
  const [lastRedemptionDays, setLastRedemptionDays] = useState<number | null>(null)

  const [redeemPoints, { loading: redeeming }] = useMutation(REDEEM_POINTS_FOR_SUPPORTER_MONTH, {
    onCompleted: async (result) => {
      setLastRedemptionDays(result.redeemPointsForSupporterMonth.supporterDaysGranted)
      setRedeemDialogOpen(false)
      toast.success(t('Points redeemed for supporter status'))
      await client.refetchQueries({ include: [POINTS_PAGE_QUERY] })
    },
    onError: (mutationError) => {
      toast.error(mutationError.message)
    },
  })

  if (networkStatus === NetworkStatus.loading) return <SFPageLoading />
  if (error) {
    return <ErrorAlert error={error} />
  }

  const balance = data.currentProfilePointBalance
  const transactions = data.currentProfilePointTransactions

  const handleRedeem = async () => {
    await redeemPoints({ variables: { idempotencyKey: crypto.randomUUID() } })
  }

  return (
    <div className="flex w-full flex-col items-stretch gap-4">
      <Card>
        <CardHeader>
          <CardTitle>{t('Points & rewards')}</CardTitle>
          <CardDescription>
            {t('Earn points from meaningful contributions and redeem them for supporter time.')}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          <div className="grid gap-3 md:grid-cols-3">
            <div>
              <div className="text-muted-foreground text-sm">{t('Available points')}</div>
              <div className="font-semibold text-3xl">{balance.availablePoints}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-sm">{t('Lifetime earned')}</div>
              <div className="font-semibold text-3xl">{balance.lifetimeEarnedPoints}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-sm">{t('Redeemed')}</div>
              <div className="font-semibold text-3xl">{balance.redeemedPoints}</div>
            </div>
          </div>

          <Separator />

          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="font-semibold text-lg">{t('Supporter month progress')}</h2>
                <p className="text-muted-foreground text-sm">
                  {balance.canRedeemSupporterMonth
                    ? t('You have enough points for one supporter month.')
                    : t('{{points}} more points needed for one supporter month.', {
                        points: balance.pointsUntilSupporterMonth,
                      })}
                </p>
              </div>
              <Button
                disabled={!balance.canRedeemSupporterMonth || redeeming}
                onClick={() => setRedeemDialogOpen(true)}
              >
                {redeeming ? <Spinner className="size-4" /> : null}
                {t('Redeem supporter month')}
              </Button>
            </div>
            <Progress value={balance.progressPercent} />
            <p className="text-muted-foreground text-sm">
              {t('{{available}} of {{threshold}} points toward {{days}} days of supporter status.', {
                available: balance.availablePoints,
                days: balance.supporterDaysGranted,
                threshold: balance.supporterMonthThreshold,
              })}
            </p>
          </div>

          {lastRedemptionDays ? (
            <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
              {t('Your redemption was recorded and {{days}} supporter days were granted.', {
                days: lastRedemptionDays,
              })}
            </div>
          ) : null}

          <Button
            className="w-fit"
            nativeButton={false}
            render={<Link to="/helpcenter/points-program" />}
            variant="link"
          >
            {t('Learn how the points program works')}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('Recent point history')}</CardTitle>
          <CardDescription>{t('Latest awards, redemptions, reversals, and admin adjustments.')}</CardDescription>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t('No point transactions yet.')}</p>
          ) : (
            <div className="flex flex-col divide-y divide-border">
              {transactions.map((transaction) => (
                <div className="flex items-start justify-between gap-3 py-3" key={transaction.id}>
                  <div>
                    <div className="font-medium">{transaction.reason}</div>
                    <div className="text-muted-foreground text-sm">
                      {t('{{type}} on {{date}}', {
                        date: dayjs(transaction.createdAt).format('MMM D, YYYY'),
                        type: transaction.type.toLowerCase(),
                      })}
                    </div>
                  </div>
                  <div className={transaction.points >= 0 ? 'font-semibold text-green-600' : 'font-semibold'}>
                    {transaction.points >= 0 ? '+' : ''}
                    {transaction.points}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        cancelText={t('Cancel')}
        confirmText={t('Redeem')}
        onConfirm={handleRedeem}
        onOpenChange={(details) => setRedeemDialogOpen(details.open)}
        open={redeemDialogOpen}
        text={t('This will spend {{points}} points and grant {{days}} days of supporter status.', {
          days: balance.supporterDaysGranted,
          points: balance.supporterMonthThreshold,
        })}
        title={t('Redeem points?')}
        variant="basic"
      />
    </div>
  )
}
